import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { downloadCSV } from '@/lib/csv';
import { buildExcelDocument } from '@/lib/excelExport';
import ClientDetailModal from './components/ClientDetailModal';

interface UnifiedClient {
  key: string;
  /** Identifiant brut du client (boutique ou carnet de crédit). */
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  date: string;
  source: 'shop' | 'credit';
  detailPath: string;
}

export default function CustomersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<UnifiedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [creatingExcel, setCreatingExcel] = useState(false);
  // Fiche client ouverte au clic (au lieu d'un saut direct dans Zifek Crédit).
  const [detailClient, setDetailClient] = useState<UnifiedClient | null>(null);

  // Filters
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchClients = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const commerceId = getCommerceId(user);

      // 1. Clients boutique enregistrés (clientshop)
      const shopQuery = supabase
        .from('clientshop')
        .select('*')
        .eq('idshop', commerceId)
        .order('date', { ascending: false });

      // 2. Clients du carnet de crédit (credit_customers) — ils n'étaient pas
      //    remontés ici, c'est pourquoi un commerce avec un carnet rempli ne
      //    voyait aucun client sur cette page.
      const creditQuery = supabase
        .from('credit_customers')
        .select('id, full_name, phone, email, created_at, merchant_id')
        .eq('merchant_id', commerceId)
        .order('created_at', { ascending: false });

      const [shopRes, creditRes] = await Promise.all([shopQuery, creditQuery]);
      if (shopRes.error) throw shopRes.error;

      const merged: UnifiedClient[] = [];

      // Clients boutique
      (shopRes.data || []).forEach((c: Record<string, unknown>) => {
        const id = c.idclient != null ? String(c.idclient) : '';
        if (!id) return;
        merged.push({
          key: `shop-${c.id ?? id}`,
          id,
          name: (c.nomclient as string) || 'Client',
          email: (c.email as string) || null,
          phone: (c.telephone as string) || null,
          address: (c.adresse as string) || null,
          date: (c.date as string) || new Date().toISOString(),
          source: 'shop',
          detailPath: `/dashboard/customer/${id}`,
        });
      });

      // Clients du carnet de crédit
      (creditRes.data || []).forEach((c: Record<string, unknown>) => {
        const id = c.id != null ? String(c.id) : '';
        if (!id) return;
        merged.push({
          key: `credit-${id}`,
          id,
          name: (c.full_name as string) || 'Client',
          email: (c.email as string) || null,
          phone: (c.phone as string) || null,
          address: null,
          date: (c.created_at as string) || new Date().toISOString(),
          source: 'credit',
          detailPath: `/dashboard/customer/credit/${id}`,
        });
      });

      merged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setClients(merged);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchClients();
  }, [fetchClients, user]);

  const filteredClients = clients.filter((c) => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${c.name} ${c.email || ''} ${c.phone || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterDateStart || filterDateEnd) {
      const d = c.date.slice(0, 10);
      if (filterDateStart && d < filterDateStart) return false;
      if (filterDateEnd && d > filterDateEnd) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  const hasActiveFilters = filterDateStart || filterDateEnd;

  const handleExportCSV = () => {
    const headers = ['Nom', 'Email', 'Téléphone', 'Adresse', 'Date d\u2019ajout', 'Source'];
    const rows = filteredClients.map((c) => [
      c.name, c.email || '', c.phone || '', c.address || '',
      new Date(c.date).toLocaleDateString('fr-FR'),
      c.source === 'credit' ? 'Carnet crédit' : 'Boutique',
    ]);
    downloadCSV('clients-shop.csv', headers, rows);
  };

  const handleOpenInExcel = async () => {
    if (!user || filteredClients.length === 0) return;
    setCreatingExcel(true);
    try {
      const headers = ['N°', 'Nom', 'Email', 'Téléphone', 'Adresse', 'Date d\u2019ajout', 'Source'];
      const rows = filteredClients.map((c, i) => [
        String(i + 1),
        c.name,
        c.email || '',
        c.phone || '',
        c.address || '',
        new Date(c.date).toLocaleDateString('fr-FR'),
        c.source === 'credit' ? 'Carnet crédit' : 'Boutique',
      ]);
      await buildExcelDocument(user.id, `Clients ${new Date().toISOString().slice(0, 10)}`, headers, rows, { sheetName: 'Clients' });
      navigate('/dashboard/file-manager');
    } catch {
      setError("Impossible de créer le fichier Excel. Réessayez.");
    } finally {
      setCreatingExcel(false);
    }
  };

  const getWaLink = (phone: string | null) => {
    if (!phone) return '';
    return `https://wa.me/${phone.replace(/[^0-9+]/g, '')}`;
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Clients</h2>
          <p className="text-sm text-foreground-500 mt-1">Liste de vos clients et leurs informations</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleOpenInExcel}
            disabled={creatingExcel || filteredClients.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-lg text-sm font-medium hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
            title="Créer une feuille Excel modifiable dans l'application Documents"
          >
            {creatingExcel ? (
              <><i className="ri-loader-4-line animate-spin"></i><span>Création...</span></>
            ) : (
              <><i className="ri-file-excel-2-line"></i><span>Ouvrir dans Excel</span></>
            )}
          </button>
          <button
            onClick={handleExportCSV}
            disabled={filteredClients.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-line"></i>
            <span>Exporter CSV</span>
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Rechercher par nom, email ou téléphone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
            showFilters || hasActiveFilters
              ? 'bg-primary-500 text-background-50 border-primary-500'
              : 'bg-background-50 text-foreground-700 border-background-200/70 hover:bg-background-100'
          }`}
        >
          <i className="ri-filter-3-line"></i>
          <span>Filtres</span>
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-accent-400"></span>
          )}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Date début</label>
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Date fin</label>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors"
            >
              <i className="ri-close-line"></i>
              Réinitialiser les filtres
            </button>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-foreground-950">{filteredClients.length}</div>
          <div className="text-xs text-foreground-500 mt-1">Total clients</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-primary-600">
            {filteredClients.filter(c => c.email).length}
          </div>
          <div className="text-xs text-foreground-500 mt-1">Avec email</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-accent-600">
            {filteredClients.filter(c => c.phone).length}
          </div>
          <div className="text-xs text-foreground-500 mt-1">Avec téléphone</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-secondary-700">
            {filteredClients.filter(c => c.source === 'credit').length}
          </div>
          <div className="text-xs text-foreground-500 mt-1">Du carnet crédit</div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-foreground-600 mb-3">{error}</p>
          <button onClick={fetchClients} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors">
            Réessayer
          </button>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-user-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-800 mb-1">Aucun client</h3>
          <p className="text-sm text-foreground-500">Les clients apparaîtront ici après leurs premiers achats</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-background-200/70 bg-background-100 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            <div className="col-span-3">Client</div>
            <div className="col-span-2">Email</div>
            <div className="col-span-2">Téléphone</div>
            <div className="col-span-2">Adresse</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-center">Actions</div>
          </div>
          <div className="divide-y divide-background-200/70">
            {filteredClients.map((client) => (
              <div key={client.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 items-center hover:bg-background-50/50 transition-colors">
                <div className="md:col-span-3 cursor-pointer" onClick={() => setDetailClient(client)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-accent-700 font-bold text-xs">{client.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-foreground-900 truncate block">{client.name}</span>
                      {client.source === 'credit' && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-primary-600">
                          <i className="ri-book-2-line"></i>Carnet crédit
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2 cursor-pointer" onClick={() => setDetailClient(client)}>
                  <span className="text-sm text-foreground-600">{client.email || '—'}</span>
                </div>
                <div className="md:col-span-2 cursor-pointer" onClick={() => setDetailClient(client)}>
                  <span className="text-sm text-foreground-600">{client.phone || '—'}</span>
                </div>
                <div className="md:col-span-2 cursor-pointer" onClick={() => setDetailClient(client)}>
                  <span className="text-sm text-foreground-600 truncate">{client.address || '—'}</span>
                </div>
                <div className="md:col-span-2 cursor-pointer" onClick={() => setDetailClient(client)}>
                  <span className="text-xs text-foreground-400">
                    {new Date(client.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="md:col-span-1 flex items-center justify-center gap-2">
                  {client.phone ? (
                    <a
                      href={getWaLink(client.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center hover:bg-green-100 transition-colors cursor-pointer"
                      title="WhatsApp"
                    >
                      <i className="ri-whatsapp-fill text-green-600 text-sm"></i>
                    </a>
                  ) : client.email ? (
                    <a
                      href={`mailto:${client.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer"
                      title="Email"
                    >
                      <i className="ri-mail-fill text-primary-600 text-sm"></i>
                    </a>
                  ) : (
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-300">
                      <i className="ri-message-2-line text-sm"></i>
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setDetailClient(client); }}
                    className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer"
                    title="Voir la fiche du client"
                  >
                    <i className="ri-file-list-3-line text-primary-600 text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {filteredClients.length > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-foreground-500">
          <span>{filteredClients.length} client{filteredClients.length > 1 ? 's' : ''}</span>
        </div>
      )}

      {detailClient && (
        <ClientDetailModal
          client={detailClient}
          rawId={detailClient.id}
          onClose={() => setDetailClient(null)}
        />
      )}
    </div>
  );
}